import {OBJ} from '../../obj';
import {ElObj, elObjOpts, ElObjOpts} from '../../elobj';
import {numberFormat, stringIterableToStringArray} from '../../util';
import {CircularProgress} from '../../ui/progress';

interface ProjectDetailStatsOpts extends ElObjOpts {
	heading: string;
}

@OBJ
export class ProjectDetailStats extends ElObj {
	private header: Header;
	private list: StatsList;

	constructor(opts: Partial<ProjectDetailStatsOpts> | null, tagName: TagName, parent?: ElObj | null);
	constructor(opts: Partial<ProjectDetailStatsOpts> | null, root: Element | null, parent?: ElObj | null);
	constructor(tagName: TagName, parent?: ElObj | null);
	constructor(root: Element | null, parent?: ElObj | null);
	constructor(opts: Partial<ProjectDetailStatsOpts> | null, tagName?: TagName);
	constructor(opts: Partial<ProjectDetailStatsOpts> | null, root?: Element | null);
	constructor(opts: Partial<ProjectDetailStatsOpts>, parent?: ElObj | null);
	constructor(opts?: Partial<ProjectDetailStatsOpts>);
	constructor(root?: Element | null);
	constructor(tagName?: TagName);
	constructor(parent?: ElObj | null);
	constructor(a?: Partial<ProjectDetailStatsOpts> | ElObj | Element | TagName | null, b?: ElObj | Element | TagName | null, c?: ElObj | null) {
		const opts = elObjOpts<ProjectDetailStatsOpts>(a, b, c);
		opts.tagName = 'div';
		super(opts);
		this.header = new Header({
			parent: this,
		});
		this.setHeading(opts.heading || 'Stats');
		this.list = new StatsList({
			parent: this,
		});
	}

	beginSync(): void {
		this.list.beginSync();
	}

	endSync(): void {
		this.list.endSync();
	}

	heading(): string {
		return this.header.text();
	}

	setHeading(heading: string): void {
		this.header.setText(heading);
	}

	setAddressCount(count: number): void {
		this.list.setAddressCount(count);
	}

	setPrice(price: number | string): void {
		this.list.setPrice(price);
	}
}

@OBJ
class Header extends ElObj {
	constructor(opts: Partial<ElObjOpts> | null, tagName: TagName, parent?: ElObj | null);
	constructor(opts: Partial<ElObjOpts> | null, root: Element | null, parent?: ElObj | null);
	constructor(tagName: TagName, parent?: ElObj | null);
	constructor(root: Element | null, parent?: ElObj | null);
	constructor(opts: Partial<ElObjOpts> | null, tagName?: TagName);
	constructor(opts: Partial<ElObjOpts> | null, root?: Element | null);
	constructor(opts: Partial<ElObjOpts>, parent?: ElObj | null);
	constructor(opts?: Partial<ElObjOpts>);
	constructor(root?: Element | null);
	constructor(tagName?: TagName);
	constructor(parent?: ElObj | null);
	constructor(a?: Partial<ElObjOpts> | ElObj | Element | TagName | null, b?: ElObj | Element | TagName | null, c?: ElObj | null) {
		const opts = elObjOpts<ElObjOpts>(a, b, c);
		const classNames = opts.classNames ?
			stringIterableToStringArray(opts.classNames) :
			[];
		opts.classNames = [
			'mdc-list-group__subheader',
			...classNames,
		];
		opts.tagName = 'h5';
		super(opts);
	}
}

@OBJ
class StatsList extends ElObj {
	private addressAnimationTimer: number | null;
	private addressCountItem: KpiItem;
	private addressCountItemProgress: CircularProgress | null;
	private currentAddressCount: number | null;
	private currentPriceDisplay: string | null;
	private priceItem: KpiItem;
	private priceItemProgress: CircularProgress | null;
	private priceSettleTimer: number | null;

	constructor(opts: Partial<ElObjOpts> | null, tagName: TagName, parent?: ElObj | null);
	constructor(opts: Partial<ElObjOpts> | null, root: Element | null, parent?: ElObj | null);
	constructor(tagName: TagName, parent?: ElObj | null);
	constructor(root: Element | null, parent?: ElObj | null);
	constructor(opts: Partial<ElObjOpts> | null, tagName?: TagName);
	constructor(opts: Partial<ElObjOpts> | null, root?: Element | null);
	constructor(opts: Partial<ElObjOpts>, parent?: ElObj | null);
	constructor(opts?: Partial<ElObjOpts>);
	constructor(root?: Element | null);
	constructor(tagName?: TagName);
	constructor(parent?: ElObj | null);
	constructor(a?: Partial<ElObjOpts> | ElObj | Element | TagName | null, b?: ElObj | Element | TagName | null, c?: ElObj | null) {
		const opts = elObjOpts<ElObjOpts>(a, b, c);
		const classNames = opts.classNames ?
			stringIterableToStringArray(opts.classNames) :
			[];
		opts.classNames = [
			'lb-project-kpi-list',
			...classNames,
		];
		opts.tagName = 'div';
		super(opts);
		this.addressAnimationTimer = null;
		this.addressCountItemProgress = null;
		this.currentAddressCount = null;
		this.currentPriceDisplay = null;
		this.addressCountItem = new KpiItem({
			label: 'Addresses',
			value: '---',
			classNames: ['kpi--addresses'],
			parent: this,
		});
		this.priceItemProgress = null;
		this.priceSettleTimer = null;
		this.priceItem = new KpiItem({
			label: 'Est. price',
			value: '---',
			classNames: ['kpi--price'],
			parent: this,
		});
	}

	beginSync(): void {
		this.destroyProgress();
		this.addressCountItem.addClass('is-syncing');
		if (this.currentAddressCount === null) {
			this.addressCountItem.setValue('---');
			this.addressCountItemProgress = new CircularProgress({
				classNames: ['kpi__progress'],
				indeterminate: true,
				size: 'small',
				parent: this.addressCountItem,
			});
			this.addressCountItemProgress.open();
		}
		this.priceItem.addClass('is-syncing');
		if (this.currentPriceDisplay === null) {
			this.priceItem.setValue('---');
			this.priceItemProgress = new CircularProgress({
				classNames: ['kpi__progress'],
				indeterminate: true,
				size: 'small',
				parent: this.priceItem,
			});
			this.priceItemProgress.open();
		}
	}

	destroy(): void {
		this.cancelAddressAnimation();
		this.clearPriceSettleTimer();
		this.destroyProgress();
		this.addressCountItem.destroy();
		this.priceItem.destroy();
		super.destroy();
	}

	private cancelAddressAnimation(): void {
		if (this.addressAnimationTimer !== null) {
			window.clearInterval(this.addressAnimationTimer);
		}
		this.addressAnimationTimer = null;
		this.addressCountItem.removeClass('is-counting');
	}

	private clearPriceSettleTimer(): void {
		if (this.priceSettleTimer !== null) {
			window.clearTimeout(this.priceSettleTimer);
		}
		this.priceSettleTimer = null;
		this.priceItem.removeClass('is-price-settling');
	}

	private destroyProgress(): void {
		if (this.addressCountItemProgress) {
			this.addressCountItemProgress.destroy();
		}
		this.addressCountItemProgress = null;
		if (this.priceItemProgress) {
			this.priceItemProgress.destroy();
		}
		this.priceItemProgress = null;
	}

	endSync(): void {
		if (this.addressCountItemProgress) {
			this.addressCountItemProgress.close();
		}
		this.destroyProgress();
		this.addressCountItem.removeClass('is-syncing');
		if (this.priceItemProgress) {
			this.priceItemProgress.close();
		}
		this.destroyProgress();
		this.priceItem.removeClass('is-syncing');
	}

	setAddressCount(count: number): void {
		const target = Math.max(0, Math.round(count));
		if (this.currentAddressCount === null || this.currentAddressCount === target) {
			this.cancelAddressAnimation();
			this.currentAddressCount = target;
			this.addressCountItem.setValue(numberFormat(target));
			return;
		}
		this.animateAddressCount(this.currentAddressCount, target);
	}

	setPrice(price: number | string): void {
		const display = `$${numberFormat(price)}`;
		const changed = this.currentPriceDisplay !== null && this.currentPriceDisplay !== display;
		this.currentPriceDisplay = display;
		this.priceItem.setValue(display);
		if (changed) {
			this.clearPriceSettleTimer();
			this.priceItem.addClass('is-price-settling');
			this.priceSettleTimer = window.setTimeout(() => {
				this.priceItem.removeClass('is-price-settling');
				this.priceSettleTimer = null;
			}, 900);
		}
	}

	private animateAddressCount(from: number, to: number): void {
		this.cancelAddressAnimation();
		this.addressCountItem.addClass('is-counting');
		const startedAt = window.performance.now();
		const duration = 760;
		const delta = to - from;
		const step = () => {
			const now = window.performance.now();
			const progress = Math.min(1, (now - startedAt) / duration);
			const eased = 1 - Math.pow(1 - progress, 3);
			const value = Math.round(from + (delta * eased));
			this.addressCountItem.setValue(numberFormat(value));
			if (progress < 1) {
				return;
			}
			if (this.addressAnimationTimer !== null) {
				window.clearInterval(this.addressAnimationTimer);
			}
			this.addressAnimationTimer = null;
			this.currentAddressCount = to;
			this.addressCountItem.setValue(numberFormat(to));
			this.addressCountItem.removeClass('is-counting');
		};
		step();
		this.addressAnimationTimer = window.setInterval(step, 32);
	}
}

interface KpiItemOpts extends ElObjOpts {
	label: string;
	value: string;
}

@OBJ
class KpiItem extends ElObj {
	private labelEl: ElObj;
	private valueEl: ElObj;

	constructor(opts: Partial<KpiItemOpts> | null, tagName: TagName, parent?: ElObj | null);
	constructor(opts: Partial<KpiItemOpts> | null, root: Element | null, parent?: ElObj | null);
	constructor(tagName: TagName, parent?: ElObj | null);
	constructor(root: Element | null, parent?: ElObj | null);
	constructor(opts: Partial<KpiItemOpts> | null, tagName?: TagName);
	constructor(opts: Partial<KpiItemOpts> | null, root?: Element | null);
	constructor(opts: Partial<KpiItemOpts>, parent?: ElObj | null);
	constructor(opts?: Partial<KpiItemOpts>);
	constructor(root?: Element | null);
	constructor(tagName?: TagName);
	constructor(parent?: ElObj | null);
	constructor(a?: Partial<KpiItemOpts> | ElObj | Element | TagName | null, b?: ElObj | Element | TagName | null, c?: ElObj | null) {
		const opts = elObjOpts<KpiItemOpts>(a, b, c);
		const classNames = opts.classNames ?
			stringIterableToStringArray(opts.classNames) :
			[];
		opts.classNames = [
			'kpi',
			...classNames,
		];
		opts.tagName = 'div';
		super(opts);
		this.labelEl = new ElObj({
			classNames: ['kpi__label'],
			parent: this,
		}, 'span');
		this.valueEl = new ElObj({
			classNames: ['kpi__value'],
			parent: this,
		}, 'span');
		this.setLabel(opts.label || '');
		this.setValue(opts.value || '---');
	}

	setLabel(label: string): void {
		this.labelEl.setText(label);
	}

	setValue(value: string): void {
		this.valueEl.setText(value);
	}
}
